import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, X, Check, Search } from 'lucide-react';

function getDateStr(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split('T')[0];
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

const QUICK_PERIODS = [
  { label: '7 dagar',  value: '7d',     days: 7 },
  { label: '30 dagar', value: '30d',    days: 30 },
  { label: '90 dagar', value: '90d',    days: 90 },
  { label: 'Anpassat', value: 'custom', days: null },
];

const STATUS_OPTIONS = [
  { value: 'Aktiv',    label: 'Aktiv' },
  { value: 'Pausad',   label: 'Pausad' },
  { value: 'Avslutad', label: 'Avslutad' },
];

const SectionLabel = ({ children }) => (
  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{children}</p>
);

const ReportFilterBar = ({ role, participants = [], handlers = [], onFilterChange }) => {
  const [quickPeriod, setQuickPeriod] = useState('30d');
  const [fromDate, setFromDate] = useState(getDateStr(30));
  const [toDate, setToDate] = useState(todayStr());
  const [handlerId, setHandlerId] = useState('');
  const [verksamhetId, setVerksamhetId] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(['Aktiv', 'Pausad', 'Avslutad']);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);

  const isCustomerAdmin = role === 'customer_admin' || role === 'super_admin';

  const isDefault =
    quickPeriod === '30d' &&
    handlerId === '' &&
    verksamhetId === '' &&
    selectedStatuses.length === STATUS_OPTIONS.length &&
    selectedParticipantIds.length === 0;

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const filter = {
      from_date: fromDate,
      to_date: toDate,
    };
    if (handlerId) filter.handler_id = handlerId;
    if (verksamhetId) filter.verksamhet_id = verksamhetId;
    if (selectedParticipantIds.length > 0) {
      filter.participant_ids = selectedParticipantIds.join(',');
    }
    if (selectedStatuses.length < STATUS_OPTIONS.length && selectedStatuses.length > 0) {
      filter.status = selectedStatuses[0];
    }
    onFilterChange(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, handlerId, verksamhetId, selectedParticipantIds, selectedStatuses]);

  const applyQuickPeriod = (qp) => {
    setQuickPeriod(qp.value);
    if (qp.days) {
      setFromDate(getDateStr(qp.days));
      setToDate(todayStr());
    }
  };

  const toggleStatus = (val) => {
    setSelectedStatuses(prev =>
      prev.includes(val) ? prev.filter(s => s !== val) : [...prev, val]
    );
  };

  const filteredParticipants = participants.filter(p =>
    (p.name || '').toLowerCase().includes(participantSearch.toLowerCase())
  );

  const toggleParticipant = (id) => {
    setSelectedParticipantIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedParticipantIds.length === participants.length && participants.length > 0) {
      setSelectedParticipantIds([]);
    } else {
      setSelectedParticipantIds(participants.map(p => p.id));
    }
  };

  const reset = () => {
    setQuickPeriod('30d');
    setFromDate(getDateStr(30));
    setToDate(todayStr());
    setHandlerId('');
    setVerksamhetId('');
    setSelectedStatuses(['Aktiv', 'Pausad', 'Avslutad']);
    setSelectedParticipantIds([]);
    setParticipantSearch('');
  };

  const allSelected = participants.length > 0 && selectedParticipantIds.length === participants.length;
  const participantLabel = selectedParticipantIds.length === 0
    ? 'Alla deltagare'
    : `${selectedParticipantIds.length} av ${participants.length} deltagare`;

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm"
      data-testid="report-filter-bar"
    >
      <div className="flex flex-wrap gap-5 items-end">

        {/* Quick period */}
        <div>
          <SectionLabel>Period</SectionLabel>
          <div className="flex border border-slate-200 rounded-md overflow-hidden">
            {QUICK_PERIODS.map(qp => (
              <button
                key={qp.value}
                onClick={() => applyQuickPeriod(qp)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  quickPeriod === qp.value
                    ? 'bg-slate-800 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`quick-period-${qp.value}`}
              >
                {qp.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom date range */}
        {quickPeriod === 'custom' && (
          <>
            <div>
              <SectionLabel>Från</SectionLabel>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white"
                data-testid="from-date-input"
              />
            </div>
            <div>
              <SectionLabel>Till</SectionLabel>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white"
                data-testid="to-date-input"
              />
            </div>
          </>
        )}

        {/* Participant multi-select */}
        {participants.length > 0 && (
          <div ref={dropRef} className="relative">
            <SectionLabel>Deltagare</SectionLabel>
            <button
              onClick={() => setDropOpen(o => !o)}
              className="flex items-center gap-1.5 h-8 px-3 text-xs border border-slate-200 rounded-md bg-white text-slate-700 hover:bg-slate-50"
              data-testid="participant-select-toggle"
            >
              {participantLabel}
              <ChevronDown size={12} />
            </button>

            {dropOpen && (
              <div
                className="absolute z-20 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg"
                data-testid="participant-dropdown"
              >
                <div className="p-2 border-b border-slate-100">
                  <div className="relative">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Sök deltagare..."
                      value={participantSearch}
                      onChange={e => setParticipantSearch(e.target.value)}
                      className="w-full h-7 pl-6 pr-2 text-xs border border-slate-200 rounded-md"
                    />
                  </div>
                </div>

                <div className="p-1">
                  <button
                    onClick={toggleSelectAll}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50 rounded"
                    data-testid="select-all-participants"
                  >
                    <span className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${
                      allSelected ? 'bg-slate-800 border-slate-800' : 'border-slate-300'
                    }`}>
                      {allSelected && <Check size={10} className="text-white" />}
                    </span>
                    Välj alla
                  </button>
                </div>

                <div className="max-h-48 overflow-y-auto p-1">
                  {filteredParticipants.map(p => (
                    <button
                      key={p.id}
                      onClick={() => toggleParticipant(p.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-50 rounded"
                    >
                      <span className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${
                        selectedParticipantIds.includes(p.id)
                          ? 'bg-slate-800 border-slate-800'
                          : 'border-slate-300'
                      }`}>
                        {selectedParticipantIds.includes(p.id) && <Check size={10} className="text-white" />}
                      </span>
                      <span className="truncate">{p.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Handler dropdown (customer_admin / super_admin only) */}
        {isCustomerAdmin && handlers.length > 0 && (
          <div>
            <SectionLabel>Handläggare</SectionLabel>
            <select
              value={handlerId}
              onChange={e => setHandlerId(e.target.value)}
              className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700"
              data-testid="handler-select"
            >
              <option value="">Alla handläggare</option>
              {handlers.map(h => (
                <option key={h.id} value={h.id}>{h.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Status */}
        <div>
          <SectionLabel>Status</SectionLabel>
          <div className="flex gap-1">
            {STATUS_OPTIONS.map(s => (
              <button
                key={s.value}
                onClick={() => toggleStatus(s.value)}
                className={`px-2 py-1 text-xs rounded font-medium transition-colors ${
                  selectedStatuses.includes(s.value)
                    ? 'bg-slate-800 text-white'
                    : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50'
                }`}
                data-testid={`status-toggle-${s.value}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Verksamhet (customer_admin / super_admin only) */}
        {isCustomerAdmin && (
          <div>
            <SectionLabel>Verksamhet</SectionLabel>
            <select
              value={verksamhetId}
              onChange={e => setVerksamhetId(e.target.value)}
              className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700"
              data-testid="verksamhet-select"
            >
              <option value="">Alla verksamheter</option>
            </select>
          </div>
        )}

        {/* Reset */}
        {!isDefault && (
          <button
            onClick={reset}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mt-5"
            data-testid="reset-filter-btn"
          >
            <X size={12} />
            Återställ filter
          </button>
        )}
      </div>
    </div>
  );
};

export default ReportFilterBar;
